import React, { useState } from 'react';
import {
  Icon,
  CheckCircle,
  Video,
  CardsThree,
  Stack,
} from '@phosphor-icons/react';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';

import { Button } from '../../../components/general';

import { isEmpty } from '../../../utils';

import AttendeePanel from './attendeePanel';
import { ExtendedEvent } from './getData';

interface Props {
  event: ExtendedEvent;
  isCreationPaid: boolean;
  isClipsPaid: boolean;
  setIsCreationPaid: React.Dispatch<React.SetStateAction<boolean>>;
  setIsClipsPaid: React.Dispatch<React.SetStateAction<boolean>>;
  isCreationAccess: boolean;
  isClipsAccess: boolean;
}

const PaymentPanel: React.FC<Props> = (props) => {
  const {
    event,
    isCreationPaid,
    isClipsPaid,
    setIsCreationPaid,
    setIsClipsPaid,
    isCreationAccess,
    isClipsAccess,
  } = props;

  const [loadingDict, setLoadingDict] = useState({
    creation: false,
    clips: false,
    bundle: false,
  });

  const handlePayment = (type: string) => {
    setLoadingDict({ ...loadingDict, [type]: true });

    setTimeout(() => {
      switch (type) {
        case 'creation':
          setIsCreationPaid(true);
          break;
        case 'clips':
          setIsClipsPaid(true);
          break;
        case 'bundle':
          setIsCreationPaid(true);
          setIsClipsPaid(true);
          break;
        default:
          break;
      }

      setLoadingDict({ ...loadingDict, [type]: false });

      toast.success('Your payment is successful!');
    }, 2000);
  };

  const btns: {
    type: 'creation' | 'clips' | 'bundle';
    Icon: Icon;
    label: string;
    description?: string;
    show: boolean;
  }[] = [
    {
      type: 'creation',
      Icon: Video,
      label: 'Buy Highlight Reel for $45',
      show: !event.payment?.paid && !isCreationPaid,
    },
    {
      type: 'clips',
      Icon: CardsThree,
      label: 'Buy Your Clips for $20',
      show: !isClipsPaid && !isEmpty(event.clips),
    },
    {
      type: 'bundle',
      Icon: Stack,
      label: 'Buy Bundle for $55',
      description: 'Highlight Reel + Clips',
      show: !isCreationAccess && !isClipsAccess && !isEmpty(event.clips),
    },
  ];

  let texts = [];

  if (isCreationPaid && isClipsPaid)
    texts.push('Your purchase of the Highlight Reel and Clips is complete.');
  else {
    if (event.payment?.paid)
      texts.push(
        `${event.organiserName ?? 'The event organiser'} has gifted you a Highlight Reel.`,
      );
    else if (isCreationPaid)
      texts.push('Your purchase of the Highlight Reel is complete.');

    if (isClipsPaid) {
      texts.push('Your purchase of the Clips is complete.');

      if (!isCreationAccess)
        texts.push('But you can still buy the Highlight Reel.');
    } else if (isCreationAccess && !isEmpty(event.clips))
      texts.push('But you can still buy your Clips.');

    if (isCreationAccess && isEmpty(event.clips))
      texts.push(
        'You will have <b>full access</b> to the files without the watermark after your video is generated.',
      );
  }

  if (isCreationAccess && isClipsAccess)
    texts.push(
      'You now have <b>full access</b> to the files without the watermark.',
    );

  return (
    <AttendeePanel
      heading="Payment"
      className="w-[30%] items-center text-center"
    >
      {isCreationAccess && (
        <React.Fragment>
          <CheckCircle weight="fill" size="3rem" color="var(--success-color)" />

          <h6>Access unlocked!</h6>
        </React.Fragment>
      )}

      {texts.map((text, index) => (
        <div key={index}>{parse(text)}</div>
      ))}

      {btns.map(
        (btn) =>
          btn.show && (
            <Button
              key={btn.type}
              onClick={() => handlePayment(btn.type)}
              colour="black-outline"
              className="w-full"
              isSubmitBtn
              isProcessing={loadingDict[btn.type]}
              spinner={{ color: 'black' }}
            >
              <div className="flex gap-2 font-semibold">
                <btn.Icon size="1.25rem" />
                <div>{btn.label}</div>
              </div>

              {btn.description && (
                <div className="text-xs italic mt-0.5">{btn.description}</div>
              )}
            </Button>
          ),
      )}
    </AttendeePanel>
  );
};

export default PaymentPanel;
